import { QueryClient } from '@tanstack/react-query';

/**
 * Centralized React Query client configuration
 *
 * Default settings:
 * - staleTime: 30s - Data is fresh for 30 seconds before refetching
 * - gcTime: 5min - Unused data is garbage collected after 5 minutes
 * - retry: 2 - Failed queries retry twice
 * - refetchOnWindowFocus: true - Refetch when window regains focus
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Query key factory for type-safe, hierarchical cache management
 *
 * Usage:
 * - queryKeys.user.profile() -> ['user', 'profile']
 * - queryKeys.user.settings() -> ['user', 'settings']
 * - queryKeys.items.all -> ['items']
 * - queryKeys.items.byId(id) -> ['items', id]
 *
 * Invalidation examples:
 * - queryClient.invalidateQueries({ queryKey: queryKeys.user.all }) // Invalidate all user queries
 * - queryClient.invalidateQueries({ queryKey: queryKeys.items.byId('123') }) // Invalidate specific item
 */
export const queryKeys = {
  // User-related queries
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    settings: () => [...queryKeys.user.all, 'settings'] as const,
  },

  // Items/resources queries (customize per project)
  // items: {
  //   all: ['items'] as const,
  //   list: (filters?: Record<string, unknown>) =>
  //     [...queryKeys.items.all, 'list', filters] as const,
  //   byId: (id: string) => [...queryKeys.items.all, id] as const,
  //   detail: (id: string) => [...queryKeys.items.all, id, 'detail'] as const,
  // },

  // Health check
  health: {
    all: ['health'] as const,
    check: () => [...queryKeys.health.all, 'check'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    fcmToken: () => [...queryKeys.notifications.all, 'fcmToken'] as const,
    settings: () => [...queryKeys.notifications.all, 'settings'] as const,
  },

  /** Stores owned by the signed-in user (typically one store per account). */
  store: {
    all: ['store'] as const,
    myStores: (uid: string) => [...queryKeys.store.all, 'my', uid] as const,
    /** Members (StoreUser) for a store — list callable is the source of truth. */
    users: (storeId: string) => [...queryKeys.store.all, 'users', storeId] as const,
    /** Staff roster (StoreEmployee) for a store. */
    employees: (storeId: string) => [...queryKeys.store.all, 'employees', storeId] as const,
    /** Catalog packages (StorePackage), including archived when requested by the hook. */
    packages: (storeId: string) => [...queryKeys.store.all, 'packages', storeId] as const,
    /** Recurring session series (StoreSession); optional `classId` filter. */
    sessions: (storeId: string, classId?: string) =>
      [...queryKeys.store.all, 'sessions', storeId, classId ?? ''] as const,
    /** Catalog classes (StoreClass). */
    classes: (storeId: string, includeArchived?: boolean) =>
      [...queryKeys.store.all, 'classes', storeId, includeArchived ? 'all' : 'active'] as const,
    /** Persisted occurrences in a UTC range (timetable). */
    sessionTimetable: (storeId: string, from: string, to: string, filtersKey: string) =>
      [...queryKeys.store.all, 'sessionTimetable', storeId, from, to, filtersKey] as const,
    /** Employee detail: upcoming occurrences + bookings (single callable). */
    employeeUpcomingOccurrences: (storeId: string, employeeId: string) =>
      [...queryKeys.store.all, 'employeeUpcomingOccurrences', storeId, employeeId] as const,
    /** Member package allowances (StoreUserPackageEntitlement). */
    userEntitlements: (storeId: string, userId: string) =>
      [...queryKeys.store.all, 'userEntitlements', storeId, userId] as const,
    /** Session bookings; filter by member via `userId` in the list callable. */
    sessionBookings: (storeId: string, userId: string) =>
      [...queryKeys.store.all, 'sessionBookings', storeId, userId] as const,
    /** Owner timetable: bookings for one occurrence (`occurrenceId`-only list callable). */
    sessionOccurrenceBookings: (storeId: string, occurrenceId: string) =>
      [...queryKeys.store.all, 'sessionOccurrenceBookings', storeId, occurrenceId] as const,
    /** Package detail: entitlements + bookings aggregated server-side. */
    packageUsageStats: (storeId: string, packageId: string) =>
      [...queryKeys.store.all, 'packageUsageStats', storeId, packageId] as const,
    /** Active entitlements awaiting pay-at-gym / cash collection (owner & staff). */
    cashCollectionPendingEntitlements: (storeId: string) =>
      [...queryKeys.store.all, 'cashCollectionPendingEntitlements', storeId] as const,
    /** Recent package pay-at-gym marks (owner & staff). */
    cashCollectionCompletedEntitlements: (storeId: string) =>
      [...queryKeys.store.all, 'cashCollectionCompletedEntitlements', storeId] as const,
    /** In-app member package payments and invoices (Stripe Checkout/Invoice). */
    payments: (storeId: string, userId?: string) =>
      [...queryKeys.store.all, 'payments', storeId, userId ?? 'all'] as const,
    /** Stripe Connect payout history for owner dashboard. */
    payouts: (storeId: string) => [...queryKeys.store.all, 'payouts', storeId] as const,
    /** Admin-authored promotional cards (StorePromotion) shown on the client home banner. */
    promotions: (storeId: string, includeArchived?: boolean) =>
      [...queryKeys.store.all, 'promotions', storeId, includeArchived ? 'all' : 'active'] as const,
    /** Outbound owner campaigns (multi-channel, targeted). */
    campaigns: (storeId: string) => [...queryKeys.store.all, 'campaigns', storeId] as const,
    /** Per-campaign delivery rows (status per recipient/channel). */
    campaignDeliveries: (storeId: string, campaignId: string) =>
      [...queryKeys.store.all, 'campaignDeliveries', storeId, campaignId] as const,
    /** Self-serve member app custom domains (Firebase Hosting). */
    customDomains: (storeId: string) => [...queryKeys.store.all, 'customDomains', storeId] as const,
    /** Current-month SMS usage rollup (debit balance + breakdown for the SMS settings page). */
    smsUsage: (storeId: string) => [...queryKeys.store.all, 'smsUsage', storeId] as const,
  },
} as const;
